import { Button } from '@/components/ui/button'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import { cn } from '@/lib/utils'
import { Download, Loader2 } from 'lucide-react'
import QRCodeLib from 'qrcode'
import { useEffect, useRef, useState } from 'react'

interface QRCodeProps {
  value: string
  size?: number
  className?: string
  showDownload?: boolean
  downloadFileName?: string
  bgColor?: string
  fgColor?: string
}

/**
 * QR Code component using qrcode npm package
 * Generates a QR code canvas for the given value
 */
export const QRCode = ({
  value,
  size = 200,
  className,
  showDownload = true,
  downloadFileName = 'qr-code',
  bgColor = '#ffffff',
  fgColor = '#000000',
}: QRCodeProps) => {
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [hasError, setHasError] = useState(false)

  useEffect(() => {
    const generateQRCode = async () => {
      if (!canvasRef.current || !value) return

      setIsLoading(true)
      setHasError(false)

      try {
        await QRCodeLib.toCanvas(canvasRef.current, value, {
          width: size,
          margin: 2,
          color: {
            dark: fgColor,
            light: bgColor,
          },
          errorCorrectionLevel: 'M',
        })
        setIsLoading(false)
      } catch (error) {
        console.error('Failed to generate QR code:', error)
        setHasError(true)
        setIsLoading(false)
      }
    }

    generateQRCode()
  }, [value, size, bgColor, fgColor])

  const handleDownload = () => {
    if (!canvasRef.current) return

    try {
      const dataUrl = canvasRef.current.toDataURL('image/png')
      const link = document.createElement('a')
      link.href = dataUrl
      link.download = `${downloadFileName}.png`
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
    } catch (error) {
      console.error('Failed to download QR code:', error)
    }
  }

  if (hasError) {
    return (
      <div
        className={cn('flex items-center justify-center bg-muted rounded-lg text-muted-foreground text-sm', className)}
        style={{ width: size, height: size }}
      >
        Failed to generate QR code
      </div>
    )
  }

  return (
    <div className={cn('flex flex-col items-center gap-3', className)}>
      <div className='relative' style={{ width: size, height: size }}>
        {isLoading && (
          <div
            className='absolute inset-0 flex items-center justify-center bg-muted rounded-lg'
            style={{ width: size, height: size }}
          >
            <Loader2 className='h-6 w-6 animate-spin text-muted-foreground' />
          </div>
        )}
        <canvas
          ref={canvasRef}
          className={cn('rounded-lg', isLoading && 'invisible')}
          style={{ width: size, height: size }}
        />
      </div>
      {showDownload && !hasError && !isLoading && (
        <Tooltip>
          <TooltipTrigger asChild>
            <Button variant='outline' size='sm' onClick={handleDownload} className='gap-2'>
              <Download className='h-4 w-4' />
              Download QR Code
            </Button>
          </TooltipTrigger>
          <TooltipContent>
            <p>Save QR code as PNG image</p>
          </TooltipContent>
        </Tooltip>
      )}
    </div>
  )
}

export default QRCode
